"use client"

import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { usePauseMediaOnHidden } from "~/lib/hooks/usePauseMediaOnHidden"

/**
 * next/image's candidate widths under the default config (deviceSizes ∪
 * imageSizes; `next.config.ts` sets no override). Used only as a fallback to
 * build a display-matched poster URL for a lazy tile whose still hasn't been
 * source-selected yet — every value here is an accepted `w`, so the derived
 * `/_next/image` URL never 404s.
 */
const NEXT_IMAGE_WIDTHS = [
  16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048, 3840,
] as const

interface LazyDemoVideoProps {
  src: string
  width: number
  height: number
  className?: string
  poster?: string
  /**
   * `sizes` for the pre-intersection / reduced-motion / Data-Saver poster,
   * which renders through `next/image`. MUST reflect the gallery tile's real
   * CSS display width across breakpoints so next/image serves a display-matched
   * candidate at every DPR (clears Lighthouse `uses-responsive-images` — the
   * DPR-descriptor `<img srcSet>` approach mis-picked the 2x file for the small
   * mobile tiles). Computed per aspect-group by the gallery page.
   */
  posterSizes?: string
  /**
   * Marks the poster as the LCP element (the first, above-the-fold gallery
   * tile). `next/image priority` preloads the optimized candidate and drops
   * lazy-loading — replacing the old raw-file HTTP `Link` header preload, which
   * double-fetched against the optimizer.
   */
  priority?: boolean
  ariaLabel: string
}

/**
 * IntersectionObserver-gated autoplay video tile used by the demo
 * gallery on `/frameworks/ads-framework`.
 *
 * Why this exists: the gallery embeds N×M `<video>` elements (one
 * per concept-format combination). Loading and decoding all of them
 * on initial paint regressed INP on mobile per the
 * CWV-in-practice research the reviewer cited — every `<video>` with
 * `preload="metadata"` fires its own TCP roundtrip and decode work
 * on the main thread.
 *
 * This component:
 * 1. Renders a `<picture>` with the poster image until the tile is
 *    within 1 viewport of the user (`rootMargin: 100%`).
 * 2. Swaps to a `<video>` element with autoplay-muted-loop the moment
 *    the tile enters that warm zone — far enough before the viewport
 *    that scroll-to-tile finishes with the video already playing.
 * 3. Respects `prefers-reduced-motion` — if the user has it set, the
 *    poster never gets replaced; the still IS the gallery for them.
 * 4. Respects `navigator.connection.saveData` — Data Saver users
 *    also see the still permanently (no autoplay video data over
 *    their constrained connection).
 *
 * SEO note: the `<video>` element renders only after hydration +
 * intersection, but the schema (`VideoObject` JSON-LD on the page)
 * is server-rendered and the poster `<img>` is server-rendered.
 * Crawlers see both the schema and the visual representation; lazy
 * mounting doesn't hide content from indexing.
 */
export function LazyDemoVideo({
  src,
  width,
  height,
  className,
  poster,
  posterSizes,
  priority,
  ariaLabel,
}: LazyDemoVideoProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  // Ref to the still's `next/image` `<img>` (next/image forwards its ref to the
  // underlying element). We read its browser-resolved `currentSrc` at mount time
  // so the `<video poster>` reuses the EXACT optimized `/_next/image?...`
  // candidate the still already fetched — a guaranteed cache hit instead of a
  // second, full-size raw-file request (r3556788645).
  const stillImgRef = useRef<HTMLImageElement>(null)
  const [shouldMount, setShouldMount] = useState(false)
  // The URL handed to `<video poster>`. Defaults to the raw poster (used only if
  // the still hasn't resolved a `currentSrc` yet); replaced at mount time with
  // the still's optimized URL so no redundant full-size poster fetch occurs.
  const [posterUrl, setPosterUrl] = useState<string | undefined>(poster)

  // Cache Components: these tiles autoplay-loop, and `display:none` (the
  // <Activity> hidden state) doesn't pause them — so without this every demo
  // tile keeps decoding video frames while the user is on another route
  // (battery/CPU drain on the heaviest gallery page). Pause on hide; resume the
  // loop on return.
  usePauseMediaOnHidden(videoRef, { resumeOnShow: true })

  useEffect(() => {
    if (!wrapperRef.current) return

    // Honour accessibility + connection preferences. If either is
    // set, never swap the poster for a `<video>` — the visible
    // still is the final state.
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    const saveData =
      typeof navigator !== "undefined" &&
      "connection" in navigator &&
      (navigator as { connection?: { saveData?: boolean } }).connection?.saveData === true
    if (prefersReducedMotion || saveData) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // Point the <video poster> at an optimized candidate BEFORE swapping
            // from the still, so no redundant raw full-size /demos/*.webp is
            // fetched (r3556788645).
            const el = stillImgRef.current
            let resolved: string | undefined
            if (el?.currentSrc) {
              // Still already source-selected (e.g. the priority/LCP tile):
              // reuse the exact optimized candidate the browser picked —
              // guaranteed cache hit, no extra variant.
              resolved = el.currentSrc
            } else if (poster) {
              // A lazy still is not source-selected yet when the observer fires
              // (rootMargin "100%" mounts tiles ~1 viewport early), so its
              // `currentSrc` is empty and `src` is next/image's full-size
              // fallback. Build the display-matched next/image candidate for
              // THIS tile's box instead, so the video poster fetches a small
              // optimized variant. Widths mirror next/image's default
              // deviceSizes∪imageSizes (no override in next.config) and q=75 is
              // the still's default quality, so the URL is a valid candidate.
              const box = wrapperRef.current?.clientWidth ?? el?.clientWidth ?? 0
              if (box > 0) {
                const needed = box * (window.devicePixelRatio || 1)
                const w =
                  NEXT_IMAGE_WIDTHS.find((c) => c >= needed) ??
                  NEXT_IMAGE_WIDTHS[NEXT_IMAGE_WIDTHS.length - 1]
                resolved = `/_next/image?url=${encodeURIComponent(poster)}&w=${w}&q=75`
              }
            }
            if (resolved) {
              setPosterUrl(resolved)
            }
            setShouldMount(true)
            observer.disconnect()
          }
        }
      },
      // 100% rootMargin = one full viewport above + below the
      // target. Tiles begin loading their video while still
      // ~1 screen away, so by the time the user scrolls into them
      // the autoplay loop is already running.
      { rootMargin: "100%" },
    )
    observer.observe(wrapperRef.current)
    return (): void => observer.disconnect()
    // `poster` is the only external value the observer callback reads. In
    // practice it is stable for a mounted tile — each gallery tile is keyed, so
    // a different demo is a remount, not a `poster` prop change — meaning the
    // effect still runs exactly once. Listing it makes the closure fresh by
    // construction instead of relying on that stability, with no behavioural
    // change on the real (never-changing) path.
  }, [poster])

  return (
    <div ref={wrapperRef} className={className} style={{ position: "absolute", inset: 0 }}>
      {shouldMount ? (
        <video
          ref={videoRef}
          src={src}
          width={width}
          height={height}
          className="h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-label={ariaLabel}
          poster={posterUrl}
        />
      ) : poster ? (
        // Pre-mount, render only the poster via next/image. This is what
        // crawlers see, what the gallery shows pre-hydration, and what
        // reduced-motion / Data-Saver users see permanently. next/image
        // optimizes the same-origin `/demos/<name>.webp` route URL (AVIF/WebP)
        // and — with the per-group `sizes` — serves a display-matched candidate
        // at every DPR, so the audit stays clean without mutating the shared
        // canonical poster file (which /watch/ pages display up to ~720px).
        <Image
          ref={stillImgRef}
          src={poster}
          alt={ariaLabel}
          fill
          sizes={posterSizes}
          priority={priority}
          className="object-cover"
        />
      ) : (
        // No poster available — render a neutral placeholder div with
        // an accessible label instead of `<img src="">`. An empty src
        // resolves to the current document URL in every browser,
        // triggering a wasteful HTML re-fetch + decode-as-image error
        // for every poster-less tile in the gallery.
        <div
          role="img"
          aria-label={ariaLabel}
          className="h-full w-full bg-slate-900/40"
        />
      )}
    </div>
  )
}
